import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  TenderPlanAllowance,
  TenderPlanUsage,
  useTenderSearchProfileAPI,
} from "../../TenderSearchProfileAPI";
import { AllowanceLevel, allowanceLevel, tenderTierLabel } from "../../tenderPlan";

interface Properties {
  /** Any change reloads the numbers (a profile created, the panel refreshed). */
  reloadKey?: unknown;
}

const LEVEL_TONE: Record<AllowanceLevel, { text: string; bar: string }> = {
  unlimited: { text: "text-muted-foreground", bar: "bg-primary" },
  notIncluded: { text: "text-muted-foreground", bar: "bg-muted" },
  normal: { text: "text-foreground", bar: "bg-primary" },
  warning: { text: "text-amber-700", bar: "bg-amber-500" },
  over: { text: "text-amber-800", bar: "bg-amber-600" },
  full: { text: "text-red-700", bar: "bg-red-600" },
};

/**
 * The customer's tender plan at a glance: tier, this month's analyses and concepts, profiles and
 * users against what the plan includes, and the auto-start cap.
 *
 * It only ever informs. A soft allowance beyond 100 % says the overage is billed — it never blocks
 * anything. A legacy customer (`tier: null`) sees nothing at all, and a failing endpoint hides the
 * panel without a word: the board must not depend on it.
 */
export default function TenderPlanUsagePanel({ reloadKey }: Properties) {
  const { t } = useTranslation();
  const api = useTenderSearchProfileAPI();
  const [usage, setUsage] = useState<TenderPlanUsage | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Through a resolved promise, so even a synchronous throw ends in the catch below.
    Promise.resolve()
      .then(() => api.getUsage())
      .then((result) => {
        if (!cancelled) setUsage(result ?? null);
      })
      .catch(() => {
        if (!cancelled) setUsage(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  if (!usage || !usage.tier) return null;

  const row = (key: string, label: string, allowance: TenderPlanAllowance | undefined, hard: boolean) => {
    if (!allowance) return null;
    const level = allowanceLevel(allowance, hard);
    const tone = LEVEL_TONE[level];
    const value =
      level === "unlimited"
        ? t("tenderPlan.usedUnlimited", { used: allowance.used })
        : level === "notIncluded"
          ? t("tenderPlan.notIncluded")
          : t("tenderPlan.usedOf", { used: allowance.used, included: allowance.included });
    const percent =
      allowance.included && allowance.included > 0
        ? Math.min(100, Math.round((allowance.used / allowance.included) * 100))
        : 0;
    const note =
      level === "over"
        ? t("tenderPlan.overage")
        : level === "full"
          ? t("tenderPlan.full")
          : level === "warning"
            ? t("tenderPlan.nearLimit")
            : null;

    return (
      <div key={key} data-testid={`tender-plan-${key}`} data-level={level} className="flex min-w-0 flex-col gap-1">
        <div className="flex items-baseline justify-between gap-2 text-xs">
          <span className="text-muted-foreground">{label}</span>
          <span className={`tabular-nums font-medium ${tone.text}`}>{value}</span>
        </div>
        {level !== "unlimited" && level !== "notIncluded" && (
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary" aria-hidden>
            <div className={`h-full ${tone.bar}`} style={{ width: `${percent}%` }} />
          </div>
        )}
        {note && <span className={`text-xs ${tone.text}`}>{note}</span>}
      </div>
    );
  };

  const autoStart = usage.autoStart;
  const autoStartText = !autoStart
    ? null
    : !autoStart.enabled
      ? t("tenderPlan.autoStart.off")
      : autoStart.monthlyCap === null || autoStart.monthlyCap === undefined
        ? t("tenderPlan.autoStart.onUncapped", { started: autoStart.startedThisPeriod })
        : t("tenderPlan.autoStart.on", {
            started: autoStart.startedThisPeriod,
            cap: autoStart.monthlyCap,
          });
  const autoStartCapReached =
    !!autoStart?.enabled &&
    typeof autoStart.monthlyCap === "number" &&
    autoStart.startedThisPeriod >= autoStart.monthlyCap;

  return (
    <section
      aria-label={t("tenderPlan.title")}
      data-testid="tender-plan-usage"
      className="mb-4 flex flex-col gap-3 rounded-md border px-4 py-3"
    >
      <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
        <span className="font-semibold">{t("tenderPlan.title")}</span>
        <span>{tenderTierLabel(usage.tier, t)}</span>
      </div>
      <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
        {row("analyses", t("tenderPlan.analyses"), usage.analyses, false)}
        {row("concepts", t("tenderPlan.concepts"), usage.concepts, false)}
        {row("profiles", t("tenderPlan.profiles"), usage.profiles, true)}
        {row("users", t("tenderPlan.users"), usage.users, true)}
      </div>
      {autoStartText && (
        <div data-testid="tender-plan-autostart" className="flex flex-wrap gap-x-2 text-xs">
          <span className="text-muted-foreground">{t("tenderPlan.autoStart.label")}</span>{" "}
          <span className="tabular-nums">{autoStartText}</span>
          {autoStartCapReached && (
            <>
              {" "}
              <span className="text-amber-700">{t("tenderPlan.autoStart.capReached")}</span>
            </>
          )}
        </div>
      )}
    </section>
  );
}
